import { useMemo, useState } from 'react';
import { Boxes, Camera, HardDrive, Search, SlidersHorizontal } from 'lucide-react';
import { Section, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useClusterInstances,
  useClusterInstance,
  useClusterInstanceAction,
  ipOf,
  roleOf,
  type ClusterInstance,
  type ClusterInstanceDetail,
  type ClusterInstanceDisk,
  type ClusterInstanceSnapshot,
} from '@/api/k8s';
import { useCreateSnapshot, useRevertSnapshot, useDeleteSnapshot } from '@/api/opennebula';
import { useOwnedZones } from '@/api/opennebula/zones';
import { RowMenu } from '../../compute/components/CrudBits';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { formatDateTime } from '../../shared';

const RUNNING = (s?: string) => { const v = String(s ?? '').toLowerCase(); return v.includes('run') || v.includes('on') || v.includes('active'); };
// Start uses OpenNebula's `resume` action — `one.vm.action` has no `poweron`
// (the compute console starts a stopped VM the same way).
type Perform = 'resume' | 'poweroff' | 'reboot' | 'terminate';
const PERFORM_LABEL: Record<Perform, string> = { resume: 'Power on', poweroff: 'Power off', reboot: 'Restart', terminate: 'Terminate' };

// Long values (name / type / image) truncate to a single line with an ellipsis so
// rows keep the dense, uniform height of the other cluster tables instead of
// wrapping onto 2–3 ragged lines. Full value stays available via the cell `title`.
const truncate = (max: number): React.CSSProperties => ({ display: 'block', maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

// Columns the user can show/hide via "Display options" (name/status/actions fixed).
const TOGGLEABLE = [
  { id: 'role', label: 'Role' },
  { id: 'ip', label: 'Assigned IP' },
  { id: 'type', label: 'Instance type' },
  { id: 'image', label: 'Image' },
  { id: 'zone', label: 'Zone' },
];

export function Instances({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { data: instances = [], isLoading } = useClusterInstances(serviceId, clusterId);
  // Zone UUID → name map from owned zones; falls back to "—" when not resolvable.
  const { ownedZones } = useOwnedZones();
  const zoneNameById = useMemo(
    () => Object.fromEntries(ownedZones.map((z) => [z.id, z.name])),
    [ownedZones],
  );
  const [target, setTarget] = useState<{ instance: ClusterInstance; perform: Perform } | null>(null);
  const [detail, setDetail] = useState<ClusterInstance | null>(null);
  const [search, setSearch] = useState('');
  // Columns the user can toggle via "Display options" — name/status/actions stay fixed.
  const [visible, setVisible] = useState<Record<string, boolean>>({ role: true, ip: true, type: true, image: true, zone: true });

  const q = search.trim().toLowerCase();
  const filtered = q ? instances.filter((i) => (i.name || String(i.id)).toLowerCase().includes(q)) : instances;

  const columns: ColumnDef<ClusterInstance, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => { const n = row.original.name || String(row.original.id); return <span title={n} style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], ...truncate(220) }}>{n}</span>; } },
    { id: 'role', header: 'Role', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', textTransform: 'capitalize' }}>{roleOf(row.original) || '—'}</span> },
    // IP lives at metadata.ip on the list payload (flat `ip` / nics[0].ip are fallbacks).
    { id: 'ip', header: 'Assigned IP', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{ipOf(row.original) || '—'}</span> },
    { id: 'type', header: 'Instance type', cell: ({ row }) => { const m = row.original.metadata; const t = m?.flavor ? `${m.flavor}${m.type ? ` (${m.type})` : ''}` : '—'; return <span title={t} style={{ color: 'var(--vn-fg-muted)', ...truncate(170) }}>{t}</span>; } },
    { id: 'image', header: 'Image', cell: ({ row }) => { const img = row.original.metadata?.image; const label = img?.name ? `${img.name}${img.distro ? ` (${img.distro})` : ''}` : '—'; return <span title={label} style={{ color: 'var(--vn-fg-muted)', ...truncate(200) }}>{label}</span>; } },
    // Zone UUID resolved to a human name; falls back to "—" (never raw UUID).
    { id: 'zone', header: 'Zone', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.zone ? (zoneNameById[row.original.zone] ?? '—') : '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => { const s = row.original.status || 'Unknown'; return <StatusPill tone={RUNNING(s) ? 'success' : 'muted'}>{s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()}</StatusPill>; } },
    {
      id: 'actions', header: '', cell: ({ row }) => {
        const on = RUNNING(row.original.status);
        return <RowMenu items={[
          { label: 'View detail', onSelect: () => setDetail(row.original) },
          ...(on ? [{ label: 'Power off', onSelect: () => setTarget({ instance: row.original, perform: 'poweroff' as const }) }] : [{ label: 'Power on', onSelect: () => setTarget({ instance: row.original, perform: 'resume' as const }) }]),
          { label: 'Restart', onSelect: () => setTarget({ instance: row.original, perform: 'reboot' as const }) },
          { label: 'Terminate', danger: true, onSelect: () => setTarget({ instance: row.original, perform: 'terminate' as const }) },
        ]} />;
      },
    },
  ];
  // Hide toggled-off columns (name/status/actions always shown).
  const shownColumns = columns.filter((c) => (c.id && TOGGLEABLE.some((t) => t.id === c.id) ? visible[c.id] : true));
  return (
    <Section title={`Cluster instances${instances.length ? ` (${instances.length})` : ''}`} description="Master and worker VMs backing this cluster.">
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? <Skeleton style={{ height: 200 }} /> : (
        <>
          <Toolbar
            start={<Input placeholder="Search instances by name" startSlot={<Search />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />}
            end={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
          <DataTable
            columns={shownColumns}
            data={filtered}
            getRowId={(i) => String(i.id)}
            onRowClick={(i) => setDetail(i)}
            emptyState={<EmptyState icon={<Boxes size={20} />} title="No instances yet" description="VMs appear once the cluster is provisioned." />}
            footer={`Showing ${filtered.length ? 1 : 0}–${filtered.length} of ${instances.length}`}
          />
        </>
      )}
      {detail && <InstanceDetailDialog serviceId={serviceId} clusterId={clusterId} instance={detail} onClose={() => setDetail(null)} onPerform={(perform) => { setTarget({ instance: detail, perform }); setDetail(null); }} />}
      {target && <InstanceActionConfirm serviceId={serviceId} clusterId={clusterId} target={target} onClose={() => setTarget(null)} />}
    </Section>
  );
}

function InstanceActionConfirm({ serviceId, clusterId, target, onClose }: { serviceId: string; clusterId: string; target: { instance: ClusterInstance; perform: Perform }; onClose: () => void }) {
  const { show } = useToast();
  const action = useClusterInstanceAction(serviceId, clusterId, target.instance.id, target.instance.metadata?.resource?.service?.service_uuid, {
    onSuccess: () => { show({ tone: 'success', title: `${PERFORM_LABEL[target.perform]} requested` }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Action failed' }),
  });
  const label = PERFORM_LABEL[target.perform];
  const name = target.instance.name || target.instance.id;
  const terminate = target.perform === 'terminate';
  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${label} instance?`}
      description={terminate
        ? `Permanently terminate “${name}”. This deletes the node VM and all data on it. Type the name to confirm.`
        : `${label} “${name}”? The node may be unavailable briefly.`}
      confirmLabel={label}
      destructive={target.perform !== 'resume'}
      requireText={terminate ? name : undefined}
      loading={action.isPending}
      onConfirm={() => action.mutate(target.perform)}
    />
  );
}

/** Rich cluster-instance detail: info, NICs, volumes with per-disk snapshot
 *  create/delete/revert (s10003 storage hooks) and terminate. Mirrors the
 *  @console KubernetesInstanceDetail page. */
function InstanceDetailDialog({ serviceId, clusterId, instance, onClose, onPerform }: {
  serviceId: string; clusterId: string; instance: ClusterInstance; onClose: () => void; onPerform: (p: Perform) => void;
}) {
  const { show } = useToast();
  const { data: detail, isLoading } = useClusterInstance(serviceId, clusterId, instance.id);
  // Zone UUID → name map; useOwnedZones is cached so calling it in both parent
  // and dialog incurs no extra network requests.
  const { ownedZones } = useOwnedZones();
  const zoneNameById = useMemo(
    () => Object.fromEntries(ownedZones.map((z) => [z.id, z.name])),
    [ownedZones],
  );
  const inst: ClusterInstanceDetail = detail ?? instance;
  const m = inst.metadata;
  const on = RUNNING(inst.status);

  const [snapTarget, setSnapTarget] = useState<{ kind: 'create'; disk: ClusterInstanceDisk } | { kind: 'revert' | 'delete'; snap: ClusterInstanceSnapshot } | null>(null);

  const createSnap = useCreateSnapshot(serviceId, instance.id, { onSuccess: () => { show({ tone: 'success', title: 'Snapshot started' }); setSnapTarget(null); }, onError: () => show({ tone: 'danger', title: 'Snapshot failed' }) });
  const revertSnap = useRevertSnapshot(serviceId, instance.id, { onSuccess: () => { show({ tone: 'success', title: 'Snapshot reverted' }); setSnapTarget(null); }, onError: () => show({ tone: 'danger', title: 'Revert failed' }) });
  const deleteSnap = useDeleteSnapshot(serviceId, instance.id, { onSuccess: () => { show({ tone: 'success', title: 'Snapshot deleted' }); setSnapTarget(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  const sysImage = m?.image?.name;
  const disks = [...(m?.disks ?? [])]
    .sort((a, b) => Number(a.disk_id) - Number(b.disk_id))
    .map((d) => ({ ...d, kind: d.image && d.image === sysImage ? 'System' : 'Mount' }));
  const snaps = m?.snapshots ?? [];
  const nics = m?.nics ?? [];

  const diskCols: ColumnDef<typeof disks[number], unknown>[] = [
    { id: 'target', header: 'Disk', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>#{row.original.disk_id}</span> },
    { id: 'name', header: 'Image', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.image || '—'}</span> },
    { id: 'type', header: 'Type', cell: ({ row }) => row.original.kind },
    { id: 'size', header: 'Size', cell: ({ row }) => { const gb = Number(row.original.size) / 1024; return Number.isFinite(gb) ? `${gb} GB` : '—'; } },
    { id: 'actions', header: '', cell: ({ row }) => row.original.kind === 'Mount' && inst.status !== 'TERMINATED'
      ? <Button variant="secondary" onClick={() => setSnapTarget({ kind: 'create', disk: row.original })}>Create snapshot</Button>
      : null },
  ];

  const snapCols: ColumnDef<ClusterInstanceSnapshot, unknown>[] = [
    { id: 'name', header: 'Snapshot', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>{row.original.name || row.original.id}</span> },
    { id: 'size', header: 'Size', cell: ({ row }) => { const gb = Number(row.original.size) / 1024; return Number.isFinite(gb) && row.original.size != null ? `${gb} GB` : '—'; } },
    { id: 'date', header: 'Created', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{snapDate(row.original.date)}</span> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[
      { label: 'Revert', onSelect: () => setSnapTarget({ kind: 'revert', snap: row.original }) },
      { label: 'Delete', danger: true, onSelect: () => setSnapTarget({ kind: 'delete', snap: row.original }) },
    ]} /> },
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle><span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>{inst.name || inst.id}<StatusPill tone={on ? 'success' : 'muted'}>{inst.status ? inst.status.charAt(0).toUpperCase() + inst.status.slice(1).toLowerCase() : '—'}</StatusPill></span></DialogTitle></DialogHeader>
        <DialogBody>
          {isLoading && !detail ? <Skeleton style={{ height: 200 }} /> : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
              <DescList minColumn={180}>
                <DescItem label="Name">{inst.name || '—'}</DescItem>
                <DescItem label="Role"><span style={{ textTransform: 'capitalize' }}>{roleOf(inst) || '—'}</span></DescItem>
                <DescItem label="Instance type">{m?.flavor ? `${m.flavor}${m.resources ? ` (${m.resources.cores ?? '?'} vCPU, ${m.resources.memory ?? '?'} GB)` : ''}` : '—'}</DescItem>
                <DescItem label="Operating system">{m?.image?.name || '—'}{m?.image?.distro ? ` (${m.image.distro})` : ''}</DescItem>
                {/* IP lives at metadata.ip on the payload (flat `ip` / nics[0].ip are fallbacks). */}
                <DescItem label="IP"><code style={monoValue}>{ipOf(inst) || '—'}</code></DescItem>
                <DescItem label="Floating IP"><code style={monoValue}>{m?.floating_ip || '—'}</code></DescItem>
                {/* Zone UUID resolved to a human name; falls back to "—" (never raw UUID). */}
                <DescItem label="Zone">{inst.zone ? (zoneNameById[inst.zone] ?? '—') : '—'}</DescItem>
                <DescItem label="Instance ID"><code style={monoValue}>{inst.id}</code></DescItem>
              </DescList>

              {nics.length > 0 && (
                <div>
                  <SubHead>Network interfaces</SubHead>
                  <DataTable
                    columns={[
                      { id: 'nic', header: 'NIC', cell: ({ row }) => `#${row.original.nic_id ?? '—'}` },
                      { id: 'ip', header: 'IP', cell: ({ row }) => <code style={monoValue}>{row.original.ip || '—'}</code> },
                      { id: 'mac', header: 'MAC', cell: ({ row }) => <code style={monoValue}>{row.original.mac || '—'}</code> },
                      { id: 'net', header: 'Network', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.network || row.original.network_id || '—'}</span> },
                    ] as ColumnDef<typeof nics[number], unknown>[]}
                    data={nics}
                    getRowId={(n, i) => String(n.nic_id ?? i)}
                  />
                </div>
              )}

              <div>
                <SubHead>Volumes</SubHead>
                {disks.length === 0 ? <EmptyState icon={<HardDrive size={20} />} title="No volumes yet" description="This instance has no mounted volumes." /> : <DataTable columns={diskCols} data={disks} getRowId={(d) => String(d.disk_id)} />}
              </div>

              <div>
                <SubHead>Volume snapshots</SubHead>
                {snaps.length === 0 ? <EmptyState icon={<Camera size={20} />} title="No snapshots yet" description="Create a snapshot from an attached volume." /> : <DataTable columns={snapCols} data={snaps} getRowId={(s) => String(s.id)} />}
                <p style={{ margin: 'var(--vn-space-2) 0 0', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Snapshots can only be reverted when the instance is stopped.</p>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                {on
                  ? <Button variant="secondary" onClick={() => onPerform('poweroff')}>Power off</Button>
                  : <Button variant="secondary" onClick={() => onPerform('resume')}>Power on</Button>}
                <Button variant="secondary" onClick={() => onPerform('reboot')}>Restart</Button>
                <Button variant="danger" onClick={() => onPerform('terminate')}>Terminate</Button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>

      <ConfirmDialog
        open={snapTarget?.kind === 'create'}
        onOpenChange={(o) => !o && setSnapTarget(null)}
        title="Create volume snapshot?"
        description={snapTarget?.kind === 'create' ? `Capture the current state of volume “${snapTarget.disk.image || `#${snapTarget.disk.disk_id}`}”.` : ''}
        confirmLabel="Create snapshot"
        loading={createSnap.isPending}
        onConfirm={() => snapTarget?.kind === 'create' && createSnap.mutate({ name: `disk-${snapTarget.disk.disk_id}-snapshot-${Date.now()}`, disk_id: snapTarget.disk.disk_id })}
      />
      <ConfirmDialog
        open={snapTarget?.kind === 'revert'}
        onOpenChange={(o) => !o && setSnapTarget(null)}
        title="Revert to snapshot?"
        description={snapTarget?.kind === 'revert' ? `Restore the volume to snapshot “${snapTarget.snap.name || snapTarget.snap.id}”. This is permanent.` : ''}
        confirmLabel="Revert"
        destructive
        loading={revertSnap.isPending}
        onConfirm={() => snapTarget?.kind === 'revert' && revertSnap.mutate(snapTarget.snap.id)}
      />
      <ConfirmDialog
        open={snapTarget?.kind === 'delete'}
        onOpenChange={(o) => !o && setSnapTarget(null)}
        title="Delete snapshot?"
        description={snapTarget?.kind === 'delete' ? `Permanently delete snapshot “${snapTarget.snap.name || snapTarget.snap.id}”.` : ''}
        confirmLabel="Delete"
        destructive
        loading={deleteSnap.isPending}
        onConfirm={() => snapTarget?.kind === 'delete' && deleteSnap.mutate(snapTarget.snap.id)}
      />
    </Dialog>
  );
}

/** Snapshot date field may be a unix-seconds number or an ISO string; normalise
 *  to ISO before passing to the shared formatDateTime helper. */
function snapDate(d?: number | string) {
  if (d == null || d === '') return '—';
  if (typeof d === 'number' || /^\d+$/.test(String(d))) {
    const n = Number(d);
    return formatDateTime(new Date(n < 1e12 ? n * 1000 : n).toISOString());
  }
  return formatDateTime(String(d));
}

function SubHead({ children }: { children: React.ReactNode }) {
  return <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>{children}</div>;
}
